/**
 * This factory creates a project manager for the given storage type.
 * The factory handles setup of the sources and channels stores
 * for the given type.
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { RemoteChannelsStore, LocalChannelsStore } from './ChannelsStore';
import ProjectManager from './ProjectManager';
import { RemoteSourcesStore, LocalSourcesStore } from './SourcesStore';
import { ProjectManagerStorageType } from '../types';
var ProjectManagerFactory = /** @class */ (function () {
    function ProjectManagerFactory() {
    }
    /**
     * Get a project manager for a specific storage type and project identitifer.
     * @param projectManagerStorageType The storage type for the project manager
     * @param projectId The identifier for the project.
     * @returns A project manager
     */
    ProjectManagerFactory.getProjectManager = function (projectManagerStorageType, projectId) {
        return new ProjectManager(this.getSourcesStore(projectManagerStorageType), this.getChannelsStore(projectManagerStorageType), projectId);
    };
    /**
     * Get a project manager for a storage type and level/script identifier (script can be undefined).
     * Fetches the channel for that level and script first, and is therefore asynchronous and could
     * throw an error if the channel request fails.
     * @param projectManagerStorageType The storage type for the project manager.
     * @param levelId The identifier for the level.
     * @param scriptId The id of the script. Can be undefined if the level is not in the context of a script.
     * @returns A project manager
     */
    ProjectManagerFactory.getProjectManagerForLevel = function (projectManagerStorageType, levelId, scriptId) {
        return __awaiter(this, void 0, void 0, function () {
            var channelsStore, channelId, response, responseBody;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        channelsStore = this.getChannelsStore(projectManagerStorageType);
                        channelId = undefined;
                        return [4 /*yield*/, channelsStore.loadForLevel(levelId, scriptId)];
                    case 1:
                        response = _a.sent();
                        if (!response.ok) return [3 /*break*/, 3];
                        return [4 /*yield*/, response.json()];
                    case 2:
                        responseBody = _a.sent();
                        if (responseBody && responseBody.channel) {
                            channelId = responseBody.channel;
                        }
                        _a.label = 3;
                    case 3:
                        if (!channelId) {
                            throw new Error('Could not load channel for level');
                        }
                        return [2 /*return*/, new ProjectManager(this.getSourcesStore(projectManagerStorageType), channelsStore, channelId)];
                }
            });
        });
    };
    ProjectManagerFactory.getSourcesStore = function (projectManagerStorageType) {
        if (projectManagerStorageType === ProjectManagerStorageType.LOCAL) {
            return new LocalSourcesStore();
        }
        else {
            return new RemoteSourcesStore();
        }
    };
    ProjectManagerFactory.getChannelsStore = function (projectManagerStorageType) {
        if (projectManagerStorageType === ProjectManagerStorageType.LOCAL) {
            return new LocalChannelsStore();
        }
        else {
            return new RemoteChannelsStore();
        }
    };
    return ProjectManagerFactory;
}());
export default ProjectManagerFactory;
